// src/pages/marketplace/admin/AdminReviewsPage.tsx

import { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { observer } from '@react';
import { useProduct, useI18n } from '@react/hooks';
import { useScope } from '@/hooks/useScope';
import {
  Button,
  Input,
  Card,
  CardContent,
  ErrorAlert,
} from '@react/components';
import { StatusBadge } from '@/components/StatusBadge';

type ReviewStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'FLAGGED';

function mapReviewStatusToVariant(
  status: string,
): 'success' | 'warning' | 'error' | 'info' | 'secondary' {
  switch (status) {
    case 'APPROVED':
      return 'success';
    case 'PENDING':
      return 'warning';
    case 'REJECTED':
      return 'error';
    case 'FLAGGED':
      return 'warning';
    default:
      return 'secondary';
  }
}

function formatDate(dateStr: string | undefined | null): string {
  if (!dateStr) return '--';
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return String(dateStr);
  }
}

function renderStars(rating: number): string {
  const filled = Math.round(Math.max(0, Math.min(5, rating)));
  const empty = 5 - filled;
  return '\u2605'.repeat(filled) + '\u2606'.repeat(empty);
}

/**
 * Review moderation queue for the product marketplace.
 * Route: /admin/products/reviews
 */
export const AdminReviewsPage = observer(() => {
  const store = useProduct();
  const { t } = useI18n();
  const scope = useScope();
  const [searchParams, setSearchParams] = useSearchParams();

  const [activeTab, setActiveTab] = useState<'all' | ReviewStatus>(
    (searchParams.get('status') as ReviewStatus) || 'all',
  );
  const [reviews, setReviews] = useState<any[]>([]);
  const [expandedReviews, setExpandedReviews] = useState<Set<string>>(new Set());
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [stats, setStats] = useState({ pending: 0, flagged: 0, today: 0, approvalRate: 0 });

  const loadReviews = useCallback(async () => {
    const result = await store.ListProductReviews(
      scope.productId,
      activeTab === 'all' ? undefined : { status: `PRODUCT_REVIEW_STATUS_${activeTab}` },
    );
    if (result) {
      const rawItems = result.reviews?.items;
      const items = Array.isArray(rawItems) ? rawItems : [];
      setReviews(items);

      // Calculate stats from loaded data
      const pendingCount = items.filter((r: any) => r.status === 'PENDING').length;
      const flaggedCount = items.filter((r: any) => r.status === 'FLAGGED').length;
      const approvedCount = items.filter((r: any) => r.status === 'APPROVED').length;
      const totalReviewed = approvedCount + items.filter((r: any) => r.status === 'REJECTED').length;
      const todayStr = new Date().toISOString().split('T')[0];
      const todayCount = items.filter(
        (r: any) => r.created_at && r.created_at.startsWith(todayStr),
      ).length;

      setStats({
        pending: pendingCount,
        flagged: flaggedCount,
        today: todayCount,
        approvalRate: totalReviewed > 0 ? Math.round((approvedCount / totalReviewed) * 100) : 0,
      });
    }
  }, [searchParams, activeTab]);

  useEffect(() => {
    loadReviews();
  }, [loadReviews]);

  const handleTabChange = (tab: 'all' | ReviewStatus) => {
    setActiveTab(tab);
    const params: Record<string, string> = {};
    if (tab !== 'all') params.status = tab;
    setSearchParams(params);
  };

  const handleAction = async (reviewId: string, action: 'APPROVED' | 'REJECTED' | 'FLAGGED', reason?: string) => {
    // No moderation RPC in the product service (only CreateProductReview/
    // ListProductReviews exist); moderation actions are intentionally hidden.
    // The previous code misused SetProductStatus with a review id, which never worked.
    void reviewId;
    void reason;
    if (action === 'REJECTED') {
      setRejectingId(null);
      setRejectReason('');
    }
    await loadReviews();
  };

  const toggleExpand = (id: string) => {
    setExpandedReviews((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const tabs: Array<{ key: 'all' | ReviewStatus; label: string; count?: number }> = [
    { key: 'all', label: 'All' },
    { key: 'PENDING', label: 'Pending', count: stats.pending },
    { key: 'APPROVED', label: 'Approved' },
    { key: 'REJECTED', label: 'Rejected' },
    { key: 'FLAGGED', label: 'Flagged', count: stats.flagged },
  ];

  return (
    <div className="space-y-6">
      {/* Breadcrumb and header */}
      <div>
        <nav className="text-sm mb-2">
          <Link to="/admin/products" className="text-muted-foreground hover:text-foreground">
            Admin
          </Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <Link to="/admin/products" className="text-muted-foreground hover:text-foreground">
            Products
          </Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <span className="text-foreground">Reviews</span>
        </nav>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Review Moderation</h1>
          <div className="flex gap-4 mt-2">
            <span className="text-sm text-muted-foreground">
              Pending:{' '}
              <span className="font-semibold text-yellow-600">{stats.pending}</span>
            </span>
            <span className="text-sm text-muted-foreground">
              Flagged:{' '}
              <span className="font-semibold text-orange-600">{stats.flagged}</span>
            </span>
          </div>
        </div>
      </div>

      {/* Quick stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-foreground">{stats.pending}</p>
            <p className="text-xs text-muted-foreground">Pending</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-foreground">{stats.flagged}</p>
            <p className="text-xs text-muted-foreground">Flagged</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-foreground">{stats.today}</p>
            <p className="text-xs text-muted-foreground">Reviews Today</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-foreground">{stats.approvalRate}%</p>
            <p className="text-xs text-muted-foreground">Approval Rate</p>
          </CardContent>
        </Card>
      </div>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      {/* Tab filter */}
      <div className="flex gap-1 border-b border-border">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => handleTabChange(tab.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.key
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
            }`}
          >
            {tab.label}
            {tab.count !== undefined && tab.count > 0 && (
              <span className="ml-2 inline-flex items-center justify-center h-5 min-w-[20px] px-1.5 rounded-full bg-destructive/10 text-destructive text-xs font-medium">
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Reviews list */}
      {store.isLoading ? (
        <div className="py-8 text-center text-muted-foreground">{t('common.loading')}</div>
      ) : reviews.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            No reviews found{activeTab !== 'all' ? ` with status "${activeTab}"` : ''}.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {reviews.map((review: any) => {
            const reviewId = review.entity?.id || review.id;
            const isExpanded = expandedReviews.has(reviewId);
            const isRejecting = rejectingId === reviewId;
            const bodyText = String(review.body ?? review.text ?? review.content ?? '');
            const shouldTruncate = bodyText.length > 300;

            return (
              <Card key={reviewId}>
                <CardContent className="p-4">
                  <div className="space-y-3">
                    {/* Review header */}
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                      <div className="flex items-start gap-3">
                        {/* Product info */}
                        {review.product?.icon_url ? (
                          <img
                            src={review.product.icon_url}
                            alt=""
                            className="h-10 w-10 rounded-md object-cover mt-0.5"
                          />
                        ) : (
                          <div className="h-10 w-10 rounded-md bg-muted flex items-center justify-center mt-0.5">
                            <span className="text-xs font-medium text-muted-foreground">
                              {String(review.product?.name ?? '').charAt(0).toUpperCase() || '?'}
                            </span>
                          </div>
                        )}
                        <div>
                          <Link
                            to={`/admin/products/${review.product_id || review.product?.id || ''}`}
                            className="text-sm font-medium text-primary hover:underline"
                          >
                            {String(review.product?.name || review.product_name || 'Unknown Product')}
                          </Link>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-sm text-foreground">
                              {String(review.author?.name || review.author_name || 'Anonymous')}
                            </span>
                            <span className="text-muted-foreground">&middot;</span>
                            <span className="text-yellow-500 text-sm tracking-wider">
                              {renderStars(review.rating ?? 0)}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">
                          {formatDate(review.created_at)}
                        </span>
                        <StatusBadge
                          variant={mapReviewStatusToVariant(review.status)}
                          label={String(review.status ?? 'UNKNOWN')}
                          size="sm"
                        />
                      </div>
                    </div>

                    {/* Review title */}
                    {(review.title || review.subject) && (
                      <h3 className="text-sm font-semibold text-foreground">
                        {String(review.title ?? review.subject ?? '')}
                      </h3>
                    )}

                    {/* Review body */}
                    <div className="text-sm text-foreground/80 leading-relaxed">
                      {shouldTruncate && !isExpanded ? (
                        <>
                          {bodyText.slice(0, 300)}...
                          <button
                            onClick={() => toggleExpand(reviewId)}
                            className="ml-1 text-primary hover:underline text-sm"
                          >
                            Show more
                          </button>
                        </>
                      ) : (
                        <>
                          {bodyText}
                          {shouldTruncate && (
                            <button
                              onClick={() => toggleExpand(reviewId)}
                              className="ml-1 text-primary hover:underline text-sm"
                            >
                              Show less
                            </button>
                          )}
                        </>
                      )}
                    </div>

                    {/* Reject reason input */}
                    {isRejecting && (
                      <div className="flex gap-2 items-end">
                        <div className="flex-1">
                          <Input
                            placeholder="Reason for rejection (required)"
                            value={rejectReason}
                            onChange={(e) => setRejectReason(e.target.value)}
                            fullWidth={false}
                            className="w-full"
                          />
                        </div>
                        <Button
                          size="sm"
                          variant="danger"
                          disabled={!rejectReason.trim()}
                          onClick={() => handleAction(reviewId, 'REJECTED', rejectReason)}
                        >
                          Confirm Reject
                        </Button>
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => {
                            setRejectingId(null);
                            setRejectReason('');
                          }}
                        >
                          Cancel
                        </Button>
                      </div>
                    )}

                    {/* Action buttons */}
                    {!isRejecting && (
                      <div className="flex gap-2 pt-1">
                        {review.status !== 'APPROVED' && (
                          <Button
                            size="sm"
                            variant="primary"
                            className="bg-green-600 hover:bg-green-700"
                            onClick={() => handleAction(reviewId, 'APPROVED')}
                          >
                            Approve
                          </Button>
                        )}
                        {review.status !== 'REJECTED' && (
                          <Button
                            size="sm"
                            variant="danger"
                            onClick={() => {
                              setRejectingId(reviewId);
                              setRejectReason('');
                            }}
                          >
                            Reject
                          </Button>
                        )}
                        {review.status !== 'FLAGGED' && (
                          <Button
                            size="sm"
                            variant="secondary"
                            className="text-yellow-600 border-yellow-300 hover:bg-yellow-50"
                            onClick={() => handleAction(reviewId, 'FLAGGED')}
                          >
                            Flag
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      <div className="flex justify-between items-center">
        <span className="text-sm text-muted-foreground">
          Showing {reviews.length} review{reviews.length !== 1 ? 's' : ''}
        </span>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            size="sm"
            disabled={!searchParams.get('cursor')}
            onClick={() => {
              const params: Record<string, string> = {};
              if (activeTab !== 'all') params.status = activeTab;
              setSearchParams(params);
            }}
          >
            First
          </Button>
          <Button
            variant="secondary"
            size="sm"
            disabled={reviews.length < 20}
            onClick={() => {
              const lastReview = reviews[reviews.length - 1];
              const cursor = lastReview?.entity?.id || lastReview?.id || '';
              if (cursor) {
                const params: Record<string, string> = { cursor };
                if (activeTab !== 'all') params.status = activeTab;
                setSearchParams(params);
              }
            }}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
});

export default AdminReviewsPage;
